import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  IconButton,
  ListSeparator,
  Menu,
  TreeDotsIcon,
  List,
  ListItem,
  AccessLogIcon,
  Dialog,
  Header3,
  CancelCrossIcon,
  DuplicateIcon,
  NotificationBellIcon,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { moveToTrash } from "~/store/actions/actions.notes";
import { getNoteById } from "~/store/selectors/selector.notes";
import { getProjectById } from "~/store/selectors/selector.projects";
import {
  getLoggedUser,
  isLoggedUserClientSelector,
  hasLoggedUserElevatedPermissions,
  makeIsLoggedUserOwnerOrProjectLeadForProject,
} from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import NoteDuplicateDialog from "./NoteDuplicateDialog";
import { NoteMoveCopyToProject } from "./NoteMoveCopyToProject";
import AccessLogs from "../AccessLogs/AccessLogs";
import { InsertLinkListItem } from "../InsertLinkListItem/InsertLinkListItem";
import { TrashOption } from "../ModalObjects";
import ReminderFormContainer from "../Reminders/ReminderFormContainer";

export const NoteOptions = ({ id, projectId, createdById, className }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showAccessLogModal, setShowAccessLogModal] = useState(false);
  const dispatch = useDispatch();
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const loggedUser = useSelector(getLoggedUser);
  const getIsLoggedUserOwnerOrProjectLeaderSelector = useMemo(makeIsLoggedUserOwnerOrProjectLeadForProject, []);
  const canSeeAccessLog = useSelector((state) => getIsLoggedUserOwnerOrProjectLeaderSelector(state, projectId));
  const showOption = !isLoggedUserClient || createdById === loggedUser.get("id");
  const hasElevetedPermissions = useSelector(hasLoggedUserElevatedPermissions);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const note = useSelector((state) => getNoteById(state, id));
  const project = useSelector((state) => getProjectById(state, projectId));

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleShowDuplicateModal = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const handleCloseDuplicateDialog = useCallback(() => {
    setShowDuplicateModal(false);
  }, []);

  const handleSubmitDuplicateDialog = useCallback(() => {
    setShowDuplicateModal(false);
    handleClose();
  }, [handleClose]);

  const handleShowAccessLog = useCallback(() => setShowAccessLogModal(true), []);
  const handleCloseAccessLog = useCallback(() => setShowAccessLogModal(false), []);

  const handleMoveToTrash = useCallback(() => {
    dispatch(moveToTrash(id));
  }, [dispatch, id]);

  const handleOpenReminder = useCallback(() => {
    handleClose();
    setShowReminderModal(true);
  }, [handleClose]);

  const handleCloseReminder = useCallback(() => {
    setShowReminderModal(false);
  }, []);

  return (
    <>
      <Menu
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        target={
          <IconButton
            variant="text gray"
            className={className}
            active={open}
          >
            <TreeDotsIcon />
          </IconButton>
        }
      >
        <List className="tw-py-2">
          <ListItem onClick={handleShowDuplicateModal}>
            <DuplicateIcon className="tw-fill-theme-900 tw-shrink-0" /> {t("Duplicate")}
          </ListItem>
          {showOption && hasElevetedPermissions && (
            <>
              <NoteMoveCopyToProject
                projectId={projectId}
                noteId={id}
                onSuccess={handleClose}
              />
              <ListSeparator />
            </>
          )}
          <InsertLinkListItem
            link={`${Angie.root_url}/projects/${projectId}/notes/${id}`}
            onCopy={handleClose}
          />
          {canSeeAccessLog && (
            <ListItem onClick={handleShowAccessLog}>
              <AccessLogIcon className="tw-fill-theme-900" />
              {t("Access Logs")}
            </ListItem>
          )}
          {!isLoggedUserClient ? (
            <ListItem onClick={handleOpenReminder}>
              <NotificationBellIcon className="tw-fill-theme-900 tw-shrink-0" />
              {t("Add Reminder")}
            </ListItem>
          ) : null}
          {showOption && (
            <>
              <ListSeparator />
              <TrashOption
                onMoveToTrash={handleMoveToTrash}
                type="note"
              />
            </>
          )}
        </List>
      </Menu>

      <NoteDuplicateDialog
        open={showDuplicateModal}
        noteId={id}
        projectId={projectId}
        onCancel={handleCloseDuplicateDialog}
        onSubmit={handleSubmitDuplicateDialog}
      />
      <Dialog
        open={showAccessLogModal}
        onClose={handleCloseAccessLog}
      >
        <Dialog.Title
          disableDefaultHeading
          className="tw-flex tw-justify-between tw-items-center"
        >
          <Header3>{t("Access Logs")}</Header3>
          <IconButton
            onClick={handleCloseAccessLog}
            variant="text gray"
          >
            <CancelCrossIcon />
          </IconButton>
        </Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <AccessLogs
            objectId={id}
            objectType="Note"
          />
        </Dialog.Content>
      </Dialog>
      {showReminderModal ? (
        <ReminderFormContainer
          open={showReminderModal}
          parent={note}
          members={project?.members.toArray()}
          onCancel={handleCloseReminder}
          onSubmit={handleCloseReminder}
        />
      ) : null}
    </>
  );
};

NoteOptions.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  createdById: PropTypes.number,
  isPinned: PropTypes.bool,
  isHiddenFromClients: PropTypes.bool,
  className: PropTypes.string,
};
